import { idToken } from '../firebase';
import { BACKEND } from './posts';
import { currentDay, loadProfile, saveProfile, type Profile } from './state';
import { markStoryOpened, storyOpened, type Storyboard } from './storyboard';

/**
 * Be in your story (well/product/32). After Day 1's story print opens, the
 * crew ask whether you want to be in it; a name and a photo later, the same
 * page comes back with you as its teller, and every picture with you in it is
 * drawn from that "me" from then on. The backend holds the photo privately
 * (kindling-cast.service.ts) and decides whether a story may be asked about
 * (`castable`); this file decides when the app asks.
 */

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await idToken();
  return fetch(`${BACKEND}${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...(init.headers || {}) } });
}

function dataUrlBlob(dataUrl: string): Blob {
  const [head, b64] = dataUrl.split(',');
  const type = /data:([^;]+)/.exec(head)?.[1] || 'image/jpeg';
  const bin = atob(b64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

/**
 * Put me in it: a name, and a photo (a data URL off the device) if there is
 * one. `redo` is "Not quite me?", the same photo once more. Throws with
 * `code` (crew_off, busy, no_redraw, too_many, …) when it cannot.
 */
export async function castMe(storyId: string, opts: { name?: string; selfie?: string; redo?: boolean; goals?: string }): Promise<Storyboard> {
  const form = new FormData();
  form.append('meta', JSON.stringify({ storyId, name: opts.name, redo: opts.redo, goals: opts.goals }));
  if (opts.selfie?.startsWith('data:image/')) form.append('selfie', dataUrlBlob(opts.selfie), 'me.jpg');
  const res = await call('/api/kindling/cast', { method: 'POST', body: form });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || `${res.status}`), { code: body.code, status: res.status });
  return body;
}

/** Take the photo off. What was drawn from it stays. */
export async function dropCastPhoto(): Promise<boolean> {
  const res = await call('/api/kindling/cast/photo', { method: 'DELETE' });
  if (!res.ok) throw new Error(`photo: ${res.status}`);
  return !!(await res.json().catch(() => ({}))).dropped;
}

/** `?cast=off` for a session that wants Day 1 as it was (32 §7): remembered until the tab closes. */
const OFF = 'kindling.castOff';
export function castOff(search = typeof location !== 'undefined' ? location.search : ''): boolean {
  try {
    const q = new URLSearchParams(search).get('cast');
    if (q === 'off') sessionStorage.setItem(OFF, '1');
    if (q === 'on') sessionStorage.removeItem(OFF);
    return sessionStorage.getItem(OFF) === '1';
  } catch { return new URLSearchParams(search).get('cast') === 'off'; }
}

/**
 * Whether to ask now: the first time the story print is opened (on the reward,
 * or from Home if the darkroom was left). "Not now" brings it back once, on a
 * later day (Home's "Your part's still open"); after a second it is not asked again.
 */
export function shouldAsk(sb: Pick<Storyboard, 'castable' | 'day'> | null | undefined, profile: Profile, off = castOff()): boolean {
  if (!sb?.castable || off || profile.crew === 'off') return false;
  const declined = profile.castDeclined ?? 0;
  return declined === 0 || (declined === 1 && currentDay(profile) > sb.day);
}

/** "Not now", counted. */
export function declineCast(): void {
  const p = loadProfile();
  saveProfile({ ...p, castDeclined: (p.castDeclined ?? 0) + 1 });
}

/** The recast print is held face-down like every other until you turn it over; each drawing of it is its own print. */
export function castKey(sb: Pick<Storyboard, 'storyId' | 'pageUrl'>): string { return `cast:${sb.storyId}:${sb.pageUrl ?? ''}`; }
export function castDry(sb: Storyboard | null | undefined): boolean {
  return !!sb && sb.cast?.status === 'cast' && !!sb.beforePageUrl && !storyOpened(castKey(sb));
}
export function markCastOpened(sb: Pick<Storyboard, 'storyId' | 'pageUrl'>): void { markStoryOpened(castKey(sb)); }

/** The print's label: "Starring Sam", or the story's own words with nobody named. */
export function starring(name?: string): string { return name ? `Starring ${name}` : 'Starring you'; }
